import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Users, Pencil, Trash2, CalendarDays } from "lucide-react";
import { toast } from "sonner";

/* Read-only viewer for a shared Meeting Documentation record. Opening it anywhere (Activities,
   Timeline, Meetings tab, save-success) shows the SAME underlying record. */
export function MeetingDocDialog({ docId, open, onClose, onChanged }) {
  const navigate = useNavigate();
  const { canRecruiting } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !docId) return;
    setLoading(true);
    api.get(`/meeting-docs/${docId}`).then((r) => setDoc(r.data))
      .catch((e) => { toast.error(apiError(e)); onClose?.(); })
      .finally(() => setLoading(false));
  }, [open, docId]);

  const del = async () => {
    setDeleting(true);
    try {
      await api.delete(`/meeting-docs/${docId}`);
      toast.success("Meeting documentation deleted");
      setConfirmDel(false); onClose?.(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setDeleting(false); }
  };

  const when = doc ? [new Date(doc.meeting_date + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }), doc.meeting_time].filter(Boolean).join(" · ") : "";

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
        <DialogContent className="sm:max-w-2xl bg-white max-h-[88vh] overflow-auto" data-testid="meeting-doc-dialog">
          {loading || !doc ? (
            <>
              <DialogHeader className="sr-only"><DialogTitle>Meeting documentation</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-400 py-10 text-center">Loading…</p>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2" data-testid="md-title">
                  <CalendarDays className="w-5 h-5 text-brand" /> {doc.title}
                </DialogTitle>
                <DialogDescription>
                  <span className="inline-flex items-center rounded-full bg-brand/10 text-brand px-2 py-0.5 text-xs font-medium mr-2">{doc.meeting_type_label}</span>
                  {when}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-1">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Attendees ({doc.attendees?.length || 0})</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="md-attendees">
                    {(doc.attendees || []).map((a) => (
                      <span key={a.person_id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">
                        <b className="font-medium">{a.name}</b>
                        {a.classification ? <span className="text-slate-400">· {a.classification}</span> : null}
                        {a.organization_name ? <span className="text-slate-400">· {a.organization_name}</span> : null}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Notes</p>
                  <div className="rounded-lg border border-slate-200 p-3 text-sm text-slate-700 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:my-2 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-brand [&_a]:underline"
                    data-testid="md-notes" dangerouslySetInnerHTML={{ __html: doc.notes_html || "" }} />
                </div>

                {doc.action_items_html ? (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Action Items / Follow-Up</p>
                    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-slate-700 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-brand [&_a]:underline"
                      data-testid="md-action-items" dangerouslySetInnerHTML={{ __html: doc.action_items_html }} />
                  </div>
                ) : null}

                <p className="text-[11px] text-slate-400">
                  Documented by {doc.created_by_name || "—"}{doc.created_at ? ` · ${new Date(doc.created_at).toLocaleString()}` : ""}
                  {doc.updated_by_name ? ` · Last updated by ${doc.updated_by_name}${doc.updated_at ? ` · ${new Date(doc.updated_at).toLocaleString()}` : ""}` : ""}
                </p>
              </div>

              {canRecruiting && doc.can_edit ? (
                <DialogFooter className="gap-2">
                  <Button variant="outline" className="text-rose-600 hover:text-rose-700" onClick={() => setConfirmDel(true)} data-testid="md-delete-btn">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                  <Button className="bg-brand hover:bg-brand-strong" onClick={() => { onClose?.(); navigate(`/calendar/document-meeting?id=${doc.id}`); }} data-testid="md-edit-btn">
                    <Pencil className="w-4 h-4 mr-1.5" /> Edit
                  </Button>
                </DialogFooter>
              ) : null}
            </>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={confirmDel} onOpenChange={setConfirmDel} title="Delete this meeting documentation?"
        description="This removes the shared record from every attendee's history. This cannot be undone."
        confirmLabel={deleting ? "Deleting…" : "Delete"} onConfirm={del} />
    </>
  );
}
